import { Head, Link } from '@inertiajs/react'

const cards = [
  {
    href: '/about',
    icon: 'architecture',
    num: '01',
    title: 'About',
    body: 'How the site is put together: architecture, Inertia flows, stack choices, and expandable technical notes.',
    cta: 'Read overview',
  },
  {
    href: '/tools',
    icon: 'data_object',
    num: '02',
    title: 'Browser tools',
    body: 'Client-side encodings, JSON, JWT, diff, hashes, Unicode normalization, and more. Each on its own page. Nothing leaves your machine.',
    cta: 'Browse tools',
  },
  {
    href: '/tools/sandbox',
    icon: 'deployed_code',
    num: '03',
    title: 'Frontend sandbox',
    body: 'Live HTML, CSS, and JavaScript with Monaco editors and a debounced preview in an isolated iframe.',
    cta: 'Launch sandbox',
  },
] as const

export default function Home() {
  return (
    <>
      <Head title="Home" />
      <div className="space-y-16 md:space-y-24">
        <section className="mx-auto max-w-5xl space-y-10 md:space-y-12" aria-labelledby="home-intro-heading">
          <h1 id="home-intro-heading" className="sr-only">
            Nogrok
          </h1>
          <div className="grid grid-cols-1 items-center gap-8 md:gap-10 lg:grid-cols-2 lg:gap-12">
            <div className="space-y-6">
              <p className="text-xl font-light leading-snug text-on-surface sm:text-2xl md:leading-snug">
                Welcome to Nogrok. A home for client-side utilities, a front-end sandbox with component library,
                and a place to host nonsense.
              </p>
              <p className="max-w-prose border-t border-outline-variant/15 pt-4 font-mono text-[11px] leading-relaxed text-on-surface-variant/90 sm:text-xs">
                This site is an ongoing work in progress. Features and content may change or disappear without
                notice; nothing here is promised or guaranteed.
              </p>
            </div>
            <aside
              className="space-y-4 border-l-2 border-primary-container/35 pl-5 md:pl-6"
              aria-label="Definition of nogrok"
            >
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="font-headline text-3xl font-bold tracking-tight text-on-surface sm:text-4xl">
                  nogrok
                </span>
                <span className="font-mono text-sm text-on-surface-variant sm:text-base">/noʊˈɡrɑk/</span>
              </div>
              <p className="font-mono text-xs leading-relaxed text-on-surface-variant sm:text-sm">
                <span className="font-semibold text-on-surface">verb</span>
                <span className="text-on-surface-variant"> · informal · contrast with </span>
                <span className="italic text-on-surface">grok</span>
              </p>
              <p className="font-mono text-[11px] leading-relaxed text-on-surface-variant/90 sm:text-xs">
                verb: nogrok; 3rd person present: nogroks; past tense: nogroked; past participle: nogroked;
                gerund or present participle: nogrokking
              </p>
              <ol className="list-decimal space-y-3 pl-5 font-body text-base leading-relaxed text-on-surface sm:text-lg">
                <li>
                  to understand only by study, logic, and effort—not by intuition or the swift merge of{' '}
                  <span className="italic">grokking</span>.
                </li>
              </ol>
              <p className="border-t border-outline-variant/20 pt-4 font-mono text-xs leading-relaxed text-on-surface-variant sm:text-sm">
                <span className="font-label font-bold tracking-widest text-on-surface">Origin</span>
                <span className="text-on-surface-variant">
                  {' '}
                  21st century: neologism pitched against <span className="italic text-on-surface">grok</span>{' '}
                  (1960s: Robert A. Heinlein, American author).
                </span>
              </p>
            </aside>
          </div>
        </section>

        <section
          className="mx-auto grid w-full max-w-[min(100%,96rem)] justify-center gap-8 [grid-template-columns:repeat(auto-fit,minmax(min(100%,16rem),22rem))]"
          aria-label="Featured areas"
        >
          {cards.map((card) => (
            <Link
              key={card.href}
              href={card.href}
              className="group flex min-h-0 min-w-0 flex-col border border-outline-variant/10 bg-surface-container-low p-8 transition-all duration-300 hover:border-primary-container/40 rounded-lg"
            >
              <div className="mb-6 flex items-start justify-between">
                <span className="material-symbols-outlined text-3xl text-primary">{card.icon}</span>
                <span className="font-mono text-[10px] text-on-surface-variant">{card.num}</span>
              </div>
              <h2 className="mb-3 font-headline text-2xl font-bold tracking-tight text-on-surface">
                {card.title}
              </h2>
              <p className="mb-8 flex-1 leading-relaxed text-on-surface-variant font-body">{card.body}</p>
              <div className="mt-auto flex items-center gap-2 font-mono text-[10px] font-bold tracking-widest text-primary uppercase">
                <span>{card.cta}</span>
                <span className="material-symbols-outlined text-sm transition-transform group-hover:translate-x-1">
                  arrow_forward
                </span>
              </div>
            </Link>
          ))}
        </section>
      </div>
    </>
  )
}
